import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Upload, FileText, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

export const OptimizationUploadPage = () => {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const processFile = async (file) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Format invalide', {
        description: 'Veuillez charger un fichier PDF uniquement.',
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadedFile(file);
    setUploadStatus(null);

    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 85) {
          clearInterval(progressInterval);
          return 85;
        }
        return prev + 15;
      });
    }, 300);

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/optimization-reports/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...getAuthHeader(),
        },
      });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadStatus('success');
      
      const shareToken = response.data.report.share_token;
      
      toast.success('Rapport transformé avec succès!', {
        description: 'Votre rapport d\'optimisation a été analysé.',
      });
      
      sessionStorage.setItem('from_upload', 'true');
      
      setTimeout(() => {
        navigate(`/optimization/report/${shareToken}`);
      }, 1500);
      
    } catch (error) {
      clearInterval(progressInterval);
      setUploadStatus('error');
      setIsUploading(false);
      toast.error('Erreur de transformation', {
        description: error.response?.data?.detail || 'Une erreur est survenue.',
      });
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      processFile(files[0]);
    }
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      processFile(files[0]);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-accent/20 to-background">
      <Navigation />
      
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">
              Charger un <span className="gradient-text">Rapport d'Optimisation</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Glissez-déposez votre rapport PDF d'optimisation de puissance
            </p>
          </div>

          <Card className="border-2">
            <CardContent className="p-8">
              <div
                className={`relative rounded-xl border-2 border-dashed transition-all p-12 ${
                  isDragging ? 'border-primary bg-accent' : 'border-border'
                } ${isUploading ? 'pointer-events-none' : 'cursor-pointer'}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={isUploading}
                />

                <div className="flex flex-col items-center gap-4">
                  {isUploading ? (
                    <>
                      <Loader2 className="w-16 h-16 text-primary animate-spin" />
                      <p className="text-lg font-semibold">Transformation en cours...</p>
                      <Progress value={uploadProgress} className="w-64" />
                      <p className="text-sm text-muted-foreground">{uploadProgress}%</p>
                    </>
                  ) : uploadStatus === 'success' ? (
                    <>
                      <CheckCircle2 className="w-16 h-16 text-success" />
                      <p className="text-lg font-semibold text-success">Succès!</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-16 h-16 text-primary" />
                      <p className="text-lg font-semibold">Glissez votre PDF ici</p>
                      <p className="text-sm text-muted-foreground">ou cliquez pour sélectionner</p>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default OptimizationUploadPage;
